import React from "react";
import VoiceTextarea from "../VoiceTextarea";

// Iter AE — Therapist Clinical Findings card.
// Captured immediately after hands-on work, while the client is still on the table.
// Saves into `outcome.therapist_findings` (structured object — see backend models).

const IMPROVEMENT_LEVELS = [
  { key: "significant", label: "Significant", tone: "bg-sage-500 text-white", inactive: "bg-paper-100 text-ink-700 hover:bg-paper-200" },
  { key: "moderate",    label: "Moderate",    tone: "bg-sage-400 text-white", inactive: "bg-paper-100 text-ink-700 hover:bg-paper-200" },
  { key: "mild",        label: "Mild",        tone: "bg-amber-400 text-ink-900", inactive: "bg-paper-100 text-ink-700 hover:bg-paper-200" },
  { key: "no_change",   label: "No change",   tone: "bg-paper-300 text-ink-900", inactive: "bg-paper-100 text-ink-700 hover:bg-paper-200" },
  { key: "worse",       label: "Worse",       tone: "bg-terracotta-500 text-white", inactive: "bg-paper-100 text-ink-700 hover:bg-paper-200" },
];

const CLINICAL_CHANGE_CHIPS = [
  { key: "pain_reduced",        label: "Pain reduced" },
  { key: "rom_improved",        label: "ROM improved" },
  { key: "less_guarding",       label: "Less guarding" },
  { key: "tissue_quality",      label: "Improved tissue quality" },
  { key: "posture",             label: "Better posture" },
  { key: "breathing",           label: "Better breathing" },
  { key: "mobility",            label: "Improved mobility" },
  { key: "neuro_symptoms",      label: "Reduced neurological symptoms" },
];

export default function TherapistFindingsCard({ findings, onChange, onSaveDraft }) {
  const f = findings || {};
  const update = (patch) => onChange({ ...f, ...patch });
  const toggleChange = (key) => {
    const set = new Set(f.clinical_changes || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    update({ clinical_changes: Array.from(set) });
  };

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 space-y-8" data-testid="therapist-findings-card">
      {/* Overall improvement */}
      <div>
        <div className="label-overline mb-3 text-sage-700">Overall response to today&rsquo;s work</div>
        <div className="flex flex-wrap gap-2" data-testid="findings-overall-group">
          {IMPROVEMENT_LEVELS.map((lvl) => {
            const active = f.overall_improvement === lvl.key;
            return (
              <button
                key={lvl.key}
                type="button"
                data-testid={`findings-overall-${lvl.key}`}
                onClick={() => { update({ overall_improvement: lvl.key }); onSaveDraft && onSaveDraft(); }}
                className={`min-h-[44px] px-5 rounded-full text-sm font-medium transition-colors ${active ? lvl.tone : lvl.inactive}`}
              >
                {lvl.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Clinical changes observed (multi-select) */}
      <div>
        <div className="label-overline mb-3 text-sage-700">Clinical changes observed</div>
        <div className="flex flex-wrap gap-2" data-testid="findings-changes-group">
          {CLINICAL_CHANGE_CHIPS.map((c) => {
            const active = (f.clinical_changes || []).includes(c.key);
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`findings-change-${c.key}`}
                onClick={() => { toggleChange(c.key); onSaveDraft && onSaveDraft(); }}
                className={`min-h-[40px] px-4 rounded-full text-sm transition-colors ${
                  active
                    ? "bg-sage-500 text-white border-sage-500"
                    : "bg-paper-50 text-ink-700 border-paper-200 hover:bg-paper-100"
                } border`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Remaining restrictions */}
      <div>
        <div className="label-overline mb-2 text-amber-700">Remaining restrictions</div>
        <p className="text-xs text-ink-500 mb-2">What&rsquo;s still tight, sensitive, or guarded after the work — these flow into the next session&rsquo;s plan.</p>
        <VoiceTextarea
          value={f.remaining_restrictions || ""}
          onChange={(v) => update({ remaining_restrictions: v })}
          onBlur={onSaveDraft}
          rows={2}
          placeholder={'"Right QL still restricted, scalene mildly guarded on overpressure."'}
          testId="findings-restrictions-input"
        />
      </div>

      {/* Assessment notes (with voice) */}
      <div>
        <div className="label-overline mb-2 text-ink-700">Therapist assessment notes</div>
        <p className="text-xs text-ink-500 mb-2">Your clinical reasoning right now — these notes ground the AI recommendations you&rsquo;re about to generate.</p>
        <VoiceTextarea
          value={f.assessment_notes || ""}
          onChange={(v) => update({ assessment_notes: v })}
          onBlur={onSaveDraft}
          rows={5}
          placeholder={'"Client responded very well to psoas, iliacus, and rib cage work. Significant reduction in lumbar guarding. Right QL remains restricted. Hand numbness likely related to scalene and pec minor involvement."'}
          testId="findings-notes-input"
        />
      </div>
    </div>
  );
}
